/**
 * Calibrator Form Modal Component
 * Create/Edit calibrator form in a dialog
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { useCreateCalibrator, useUpdateCalibrator } from '../hooks';
import type { Calibrator } from '../types';
import { CALIBRATOR_TYPES } from '../types';

const DEFAULT_CALIBRATOR_TYPE = 4;

interface CalibratorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  calibrator?: Calibrator;
}

export function CalibratorModal({
  open,
  onOpenChange,
  calibrator,
}: CalibratorModalProps) {
  const isEditing = !!calibrator;

  // ---------------------------------------------------------------------------
  // Form state (controlled useState pattern)
  // ---------------------------------------------------------------------------
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [type, setType] = useState(DEFAULT_CALIBRATOR_TYPE);
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [unit, setUnit] = useState('');
  const [resolution, setResolution] = useState('');
  const [measurementUncertainty, setMeasurementUncertainty] = useState('');
  const [certNumber, setCertNumber] = useState('');
  const [certIssueDate, setCertIssueDate] = useState('');
  const [certDueDate, setCertDueDate] = useState('');

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------
  const createMutation = useCreateCalibrator();
  const updateMutation = useUpdateCalibrator();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // ---------------------------------------------------------------------------
  // Reset form when modal opens/closes or calibrator changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (open && calibrator) {
      setName(calibrator.name);
      setEnabled(calibrator.enabled);
      setType(calibrator.type);
      setManufacturer(calibrator.manufacturer || '');
      setModel(calibrator.model || '');
      setSerialNumber(calibrator.serialNumber || '');
      setUnit(calibrator.unit);
      setResolution(calibrator.resolution);
      setMeasurementUncertainty(calibrator.measurementUncertainty);
      setCertNumber(calibrator.certNumber);
      setCertIssueDate(calibrator.certIssueDate ? calibrator.certIssueDate.slice(0, 10) : '');
      setCertDueDate(calibrator.certDueDate ? calibrator.certDueDate.slice(0, 10) : '');
    } else if (open && !calibrator) {
      setName('');
      setEnabled(true);
      setType(DEFAULT_CALIBRATOR_TYPE);
      setManufacturer('');
      setModel('');
      setSerialNumber('');
      setUnit('');
      setResolution('');
      setMeasurementUncertainty('');
      setCertNumber('');
      setCertIssueDate('');
      setCertDueDate('');
    }
  }, [open, calibrator]);

  // ---------------------------------------------------------------------------
  // Submit handler
  // ---------------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      name,
      enabled,
      type,
      manufacturer: manufacturer || undefined,
      model: model || undefined,
      serial_number: serialNumber || undefined,
      unit,
      resolution: Number(resolution),
      measurement_uncertainty: Number(measurementUncertainty),
      cert_number: certNumber,
      cert_issue_date: certIssueDate,
      cert_due_date: certDueDate,
    };

    if (isEditing) {
      await updateMutation.mutateAsync({
        id: calibrator.mongoCalibratorId,
        data: payload,
      });
    } else {
      await createMutation.mutateAsync(payload);
    }

    onOpenChange(false);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Calibrator' : 'Create Calibrator'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="cal-name">Name *</Label>
            <Input
              id="cal-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Calibrator name"
            />
          </div>

          {/* Enabled + Type row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cal-enabled">Enabled</Label>
              <div className="flex items-center gap-2 pt-1">
                <Switch
                  id="cal-enabled"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                />
                <span className="text-sm text-muted-foreground">
                  {enabled ? 'Active' : 'Disabled'}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cal-type">Type *</Label>
              <Select
                value={String(type)}
                onValueChange={(v) => setType(Number(v))}
              >
                <SelectTrigger id="cal-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CALIBRATOR_TYPES).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Manufacturer + Model row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cal-manufacturer">Manufacturer</Label>
              <Input
                id="cal-manufacturer"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                placeholder="e.g. Fluke"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cal-model">Model</Label>
              <Input
                id="cal-model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="e.g. 5520A"
              />
            </div>
          </div>

          {/* Serial Number */}
          <div className="space-y-2">
            <Label htmlFor="cal-serial">Serial Number</Label>
            <Input
              id="cal-serial"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="Serial number"
            />
          </div>

          {/* Unit + Resolution + Uncertainty row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cal-unit">Unit *</Label>
              <Input
                id="cal-unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                required
                placeholder="e.g. C"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cal-resolution">Resolution *</Label>
              <Input
                id="cal-resolution"
                type="number"
                step="any"
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                required
                placeholder="0.01"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cal-uncertainty">Uncertainty *</Label>
              <Input
                id="cal-uncertainty"
                type="number"
                step="any"
                value={measurementUncertainty}
                onChange={(e) => setMeasurementUncertainty(e.target.value)}
                required
                placeholder="0.05"
              />
            </div>
          </div>

          {/* Certificate fields */}
          <div className="space-y-2">
            <Label htmlFor="cal-cert-number">Certificate Number *</Label>
            <Input
              id="cal-cert-number"
              value={certNumber}
              onChange={(e) => setCertNumber(e.target.value)}
              required
              placeholder="Certificate number"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cal-cert-issue">Issue Date *</Label>
              <Input
                id="cal-cert-issue"
                type="date"
                value={certIssueDate}
                onChange={(e) => setCertIssueDate(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cal-cert-due">Due Date *</Label>
              <Input
                id="cal-cert-due"
                type="date"
                value={certDueDate}
                onChange={(e) => setCertDueDate(e.target.value)}
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
